import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { PaperPlaneTilt, CaretLeft, Archive, Tray, Flag, Warning, X } from "@phosphor-icons/react";
import { api, imgUrl, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const REPORT_REASONS = [
  "Sending offers on a FIRM listing",
  "Harassment or abusive language",
  "Spam or unsolicited promotion",
  "Suspected scam / phishing",
  "Sharing personal contact info to move off-platform",
  "Threatening or intimidating messages",
  "Other",
];

export default function Conversation() {
  const { convoId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [other, setOther] = useState(null);
  const [listing, setListing] = useState(null);
  const [archived, setArchived] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [reportTarget, setReportTarget] = useState(null); // message being reported
  const [reportReason, setReportReason] = useState(REPORT_REASONS[0]);
  const [reportDetail, setReportDetail] = useState("");
  const [reportBusy, setReportBusy] = useState(false);
  const endRef = useRef();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/messages/conversations/${convoId}`);
      setMessages(data.messages || []);
      setOther(data.other_user || null);
      setListing(data.listing || null);
      setArchived(Boolean(data.conversation?.archived));
    } catch (err) { toast.error(formatErr(err)); }
    finally { setLoading(false); }
  }, [convoId]);

  useEffect(() => {
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post(`/messages/conversations/${convoId}/send`, { text: text.trim() });
      setMessages((m) => [...m, data.message]);
      setText("");
      // Sending unarchives the thread for both sides on the server; reflect it locally.
      if (archived) setArchived(false);
    } catch (err) { toast.error(formatErr(err)); }
    finally { setSending(false); }
  };

  const toggleArchive = async () => {
    setArchiving(true);
    try {
      const endpoint = archived ? "unarchive" : "archive";
      await api.post(`/messages/conversations/${convoId}/${endpoint}`);
      toast.success(archived ? "Conversation moved back to Inbox" : "Conversation archived");
      if (archived) {
        setArchived(false);
      } else {
        // Archived from Inbox — bounce back to the messages list.
        navigate("/messages");
      }
    } catch (err) { toast.error(formatErr(err)); }
    finally { setArchiving(false); }
  };

  const openReport = (msg) => {
    setReportTarget(msg);
    setReportReason(REPORT_REASONS[0]);
    setReportDetail("");
  };

  const submitReport = async () => {
    if (!reportTarget) return;
    if (reportDetail.trim().length < 5) {
      toast.error("Add a short comment (5+ chars) explaining the issue.");
      return;
    }
    setReportBusy(true);
    try {
      await api.post("/reports", {
        target_type: "message",
        target_id: reportTarget.id,
        reason: reportReason,
        detail: reportDetail.trim(),
      });
      toast.success("Report submitted — admins will review shortly.");
      setReportTarget(null);
    } catch (err) { toast.error(formatErr(err)); }
    finally { setReportBusy(false); }
  };

  // FIRM listings do not accept offers — sending an offer message here is a
  // strike-worthy violation of community rules. Show a persistent warning
  // above the composer so the sender is on notice before hitting Send.
  const isFirmListing = listing && listing.accepts_offers === false;

  return (
    <div className="max-w-3xl mx-auto px-3 md:px-8 py-2 md:py-6 flex flex-col h-[calc(100vh-8rem)] md:h-[calc(100vh-6rem)]">
      <div className="flex items-center gap-3 pb-3 border-b border-white/10">
        <Link to="/messages" data-testid="convo-back" className="text-zinc-400 hover:text-white"><CaretLeft size={20} weight="bold" /></Link>
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold flex items-center gap-2">
            @{other?.username || "?"}
            {archived && <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 border border-white/20 px-1.5 py-0.5" data-testid="convo-archived-badge">Archived</span>}
          </div>
          {listing?.title && (
            <Link to={`/listing/${listing.id}`} data-testid="convo-listing-link" className="font-mono text-[10px] text-zinc-500 uppercase truncate hover:text-[#FF5F1F] block">
              Re: {listing.title} — ${listing.price}
            </Link>
          )}
        </div>
        <button
          onClick={toggleArchive}
          disabled={archiving}
          data-testid={archived ? "convo-unarchive" : "convo-archive"}
          title={archived ? "Move back to Inbox" : "Archive this conversation"}
          className="text-zinc-400 hover:text-[#FF5F1F] transition-colors p-1 shrink-0"
        >
          {archived ? <Tray size={20} weight="bold" /> : <Archive size={20} weight="bold" />}
        </button>
        {listing?.front_image && (
          <Link to={`/listing/${listing.id}`}>
            <img src={imgUrl(listing.front_image, { w: 96 })} alt="" className="w-10 h-10 object-cover border border-white/10 shrink-0" loading="lazy" decoding="async" />
          </Link>
        )}
      </div>

      <div className="flex-1 overflow-y-auto py-4 space-y-3">
        {loading ? (
          <div className="text-center font-mono text-xs uppercase tracking-widest text-zinc-500">Loading…</div>
        ) : messages.length === 0 ? (
          <div className="text-center font-mono text-xs uppercase tracking-widest text-zinc-500 py-16">No messages yet.</div>
        ) : (
          messages.map((m) => {
            const mine = m.sender_id === user.id;
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`message-${m.id}`}>
                <div className={`group relative max-w-[80%] px-3 py-2 border ${mine ? "bg-[#FF5F1F] border-[#FF5F1F] text-white" : "bg-[#18181B] border-white/10 text-zinc-100"}`}>
                  <div className="text-sm whitespace-pre-wrap break-words">{m.text}</div>
                  {!mine && (
                    <button
                      type="button"
                      onClick={() => openReport(m)}
                      data-testid={`report-message-${m.id}`}
                      title="Report this message"
                      aria-label="Report this message"
                      className="absolute -right-2 -bottom-2 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity bg-[#0A0A0A] border border-white/20 text-zinc-400 hover:text-red-400 hover:border-red-400 w-6 h-6 flex items-center justify-center"
                    >
                      <Flag size={11} weight="bold" />
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
        <div ref={endRef} />
      </div>

      {isFirmListing && (
        <div
          data-testid="firm-warning-banner"
          className="mt-2 mb-1 flex items-start gap-2 border border-[#FFD700]/50 bg-[#FFD700]/5 px-3 py-2 text-[11px] leading-relaxed text-[#FFD700]"
        >
          <Warning size={16} weight="fill" className="shrink-0 mt-0.5" />
          <div>
            <span className="font-mono uppercase tracking-widest text-[10px] font-bold">FIRM listing</span>
            <span className="text-zinc-300"> — this seller is not accepting offers. Sending an offer anyway may result in an account strike and could lead to suspension or removal from Hunt57.</span>
          </div>
        </div>
      )}

      <form onSubmit={send} className="pt-3 border-t border-white/10 flex gap-2">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          className="h57-input flex-1"
          placeholder={isFirmListing ? "Say hi — but no offers on FIRM listings" : "Type a message…"}
          data-testid="convo-input"
        />
        <button type="submit" disabled={sending || !text.trim()} className="h57-btn-primary !px-4" data-testid="convo-send">
          <PaperPlaneTilt size={16} weight="bold" />
        </button>
      </form>

      {/* Report Message modal */}
      {reportTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => !reportBusy && setReportTarget(null)}>
          <div className="w-full max-w-md bg-[#121212] border border-white/10 p-6" onClick={(e) => e.stopPropagation()} data-testid="report-message-modal">
            <div className="flex items-start justify-between gap-3">
              <h3 className="font-display font-black uppercase text-lg">Report Message</h3>
              <button
                type="button"
                onClick={() => setReportTarget(null)}
                disabled={reportBusy}
                data-testid="report-modal-close"
                className="text-zinc-500 hover:text-white p-1"
              ><X size={18} weight="bold" /></button>
            </div>
            <p className="text-xs text-zinc-500 mt-1">From @{other?.username || "user"}</p>

            <div className="mt-4 p-3 bg-[#0A0A0A] border border-white/10 text-sm text-zinc-300 whitespace-pre-wrap break-words max-h-32 overflow-y-auto" data-testid="report-message-preview">
              {reportTarget.text}
            </div>

            <label className="h57-label mt-4">Reason</label>
            <select
              className="h57-input"
              value={reportReason}
              onChange={(e) => setReportReason(e.target.value)}
              data-testid="report-reason-select"
              disabled={reportBusy}
            >
              {REPORT_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>

            <label className="h57-label mt-4">Comments <span className="text-zinc-500 normal-case tracking-normal">(required)</span></label>
            <textarea
              value={reportDetail}
              onChange={(e) => setReportDetail(e.target.value)}
              className="h57-input min-h-[110px]"
              placeholder="Give admins the context — what happened, why it violates community rules, any prior back-and-forth…"
              maxLength={1000}
              data-testid="report-detail-input"
              disabled={reportBusy}
            />
            <div className="mt-1 font-mono text-[10px] text-zinc-500 tracking-widest uppercase text-right tabular-nums">
              {reportDetail.length}/1000
            </div>

            <div className="mt-4 flex gap-3">
              <button
                type="button"
                onClick={() => setReportTarget(null)}
                disabled={reportBusy}
                className="h57-btn-secondary flex-1"
                data-testid="report-cancel"
              >Cancel</button>
              <button
                type="button"
                onClick={submitReport}
                disabled={reportBusy || reportDetail.trim().length < 5}
                className="h57-btn-primary flex-1"
                data-testid="report-submit"
              >
                {reportBusy ? "Submitting…" : "Submit Report"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
